import { StrictMode, useEffect, type ReactElement } from 'react';
import { createRoot } from 'react-dom/client';
import './styles.css';
import { Leaderboard, LeaderboardSkeleton, type LeaderboardData } from './components/Leaderboard';
import { BrandStats, BrandStatsSkeleton, type BrandStatsData } from './components/BrandStats';
import { ErrorState } from './components/ui/States';
import { Demo } from './routes/Demo';
import { useHost } from './lib/useHost';
import { applyTheme } from './lib/theme';

/**
 * Single-file router. Each entry HTML can pin its route via
 * `<meta name="widget-route" content="...">` so the inlined bundle for
 * production renders the right widget regardless of URL. In dev (`bun run
 * dev`), we fall back to `window.location.pathname`.
 *
 * Routes:
 *   /demo            — gallery with sample data (standalone)
 *   /leaderboard     — list_top_brands_in_* tools
 *   /brand-stats     — get_brand_stats tool
 */
function App() {
  const route = currentRoute();
  if (route === 'demo') return <Demo />;
  if (route === 'leaderboard') return <RouteShell kind="leaderboard" />;
  if (route === 'brand-stats') return <RouteShell kind="brand-stats" />;
  return <NotFound route={route} />;
}

type Route = 'demo' | 'leaderboard' | 'brand-stats' | 'unknown';

function currentRoute(): Route {
  if (typeof document !== 'undefined') {
    const meta = document.querySelector('meta[name="widget-route"]');
    const v = meta?.getAttribute('content');
    if (v === 'demo' || v === 'leaderboard' || v === 'brand-stats') return v;
  }
  if (typeof window === 'undefined') return 'unknown';
  const p = window.location.pathname;
  if (p === '/' || p === '/demo' || p.endsWith('/demo.html')) return 'demo';
  if (p === '/leaderboard' || p.endsWith('/leaderboard.html')) return 'leaderboard';
  if (p === '/brand-stats' || p.endsWith('/brand-stats.html')) return 'brand-stats';
  return 'unknown';
}

function RouteShell({ kind }: { kind: 'leaderboard' | 'brand-stats' }): ReactElement {
  const { host, data, theme } = useHost();

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  if (data === undefined || data === null) {
    return <Frame>{kind === 'leaderboard' ? <LeaderboardSkeleton /> : <BrandStatsSkeleton />}</Frame>;
  }

  // Runtime guard — a malformed payload renders an error card rather than
  // crashing the whole iframe.
  try {
    if (kind === 'leaderboard') {
      const d = data as LeaderboardData;
      if (!Array.isArray(d.items)) throw new Error('Missing items[] in leaderboard payload');
      return (
        <Frame>
          <Leaderboard data={d} host={host} />
        </Frame>
      );
    }
    const d = data as BrandStatsData;
    if (typeof d.visibility_score !== 'number') throw new Error('Missing visibility_score');
    return (
      <Frame>
        <BrandStats data={d} host={host} />
      </Frame>
    );
  } catch (err) {
    return (
      <Frame>
        <ErrorState message={err instanceof Error ? err.message : String(err)} />
      </Frame>
    );
  }
}

function Frame({ children }: { children: ReactElement | ReactElement[] }) {
  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-2xl mx-auto">{children}</div>
    </div>
  );
}

function NotFound({ route }: { route: string }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-surface border border-border rounded-card p-5 text-sm">
        <div className="text-[10px] uppercase tracking-wider text-muted font-medium mb-2">Widget not found</div>
        <div className="text-primary">
          No widget at <code className="font-mono">{route}</code>. Try /demo, /leaderboard, or /brand-stats.
        </div>
      </div>
    </div>
  );
}

const root = document.getElementById('root');
if (!root) throw new Error('No #root element on page');
createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
